import { Injectable } from 'framework/di';
import { pipe } from 'framework/functional';

import {
  RecommendationPayload,
  RecommendationQueryParams,
  RecommendationResponse,
} from '../../providers/boost';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { B2BService } from '../b2b/b2b.service';
import { PlatformLoader } from '../platform/platform.loader';

/**
 * API service for handling recommendation widget requests to the Boost recommendation engine.
 *
 * This service extends {@link BoostAPI} to provide specialized functionality for fetching
 * product recommendations from the Boost API. It manages request construction through
 * extensible protected methods that allow Technical Support teams to customize query
 * parameters and body payloads for shop-specific requirements.
 *
 * The service uses a functional pipeline approach to transform request parameters,
 * applying currency settings and B2B configurations automatically while allowing
 * additional customizations through method overrides.
 *
 * @remarks
 * Key extensibility features:
 * - Protected methods for query parameter and body payload customization
 * - Automatic currency and B2B parameter injection
 * - Functional composition pipeline for parameter transformation
 * - Inherits all HTTP client capabilities from {@link BoostAPI}
 *
 * Performance considerations:
 * - Uses functional pipes for efficient parameter transformation
 * - Minimal overhead for parameter building
 * - Leverages inherited HTTP client connection pooling
 *
 * Technical Support teams can extend this service to:
 * - Add shop-specific query parameters (pricing tiers, customer segments)
 * - Modify request body payloads with custom filters or context
 * - Implement request/response transformations
 * - Add custom error handling or retry logic
 * - Integrate shop-specific analytics or tracking
 *
 * @example
 * Basic usage in a recommendation widget:
 * ```typescript
 * const api = new RecommendationAPI(platformLoader, appService, b2bService);
 * const response = await api.getRecommendation(
 *   { limit: 10, page: 1 },
 *   { productId: '12345', type: 'similar' }
 * );
 * ```
 *
 * @example
 * Extend to add custom query parameters for VIP customers:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationAPI', (RecommendationAPI) => {
 *   return class CustomRecommendationAPI extends RecommendationAPI {
 *     protected buildRecommendationQueryParams(params) {
 *       const enhanced = super.buildRecommendationQueryParams(params);
 *
 *       // Add VIP pricing tier for wholesale customers
 *       const customer = this.platformLoader.platform.customer;
 *       if (customer?.tags?.includes('wholesale')) {
 *         enhanced.pricing_tier = 'wholesale';
 *         enhanced.discount_level = 'tier-1';
 *       }
 *
 *       return enhanced;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Extend to add custom filtering to request body:
 * ```javascript
 * window.boostWidgetIntegration.extend('RecommendationAPI', (RecommendationAPI) => {
 *   return class CustomRecommendationAPI extends RecommendationAPI {
 *     protected buildRecommendationBodyPayload(bodyPayload) {
 *       const enhanced = super.buildRecommendationBodyPayload(bodyPayload);
 *
 *       // Exclude out-of-stock and discontinued products
 *       enhanced.filters = {
 *         ...enhanced.filters,
 *         excludeTags: ['out-of-stock', 'discontinued'],
 *         inStock: true
 *       };
 *
 *       // Add customer segment for personalization
 *       enhanced.customerSegment = this.getCustomerSegment();
 *
 *       return enhanced;
 *     }
 *
 *     getCustomerSegment() {
 *       const customer = this.platformLoader.platform.customer;
 *       if (customer?.ordersCount > 10) return 'loyal';
 *       if (customer?.ordersCount > 0) return 'returning';
 *       return 'new';
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class RecommendationAPI extends BoostAPI {
  /**
   * Maps platform page types to API parameter values for page-specific recommendations.
   *
   * This mapping converts internal page identifiers to the format expected by the
   * Boost recommendation API's `pg` parameter. The API uses these values to optimize
   * recommendations based on the current page context.
   *
   * @example
   * Extend to add custom page types:
   * ```typescript
   * class CustomRecommendationAPI extends RecommendationAPI {
   *   PAGE_PARAM_MAP = {
   *     ...super.PAGE_PARAM_MAP,
   *     'landing': 'landing_page',
   *     'about': 'info_page'
   *   };
   * }
   * ```
   */
  PAGE_PARAM_MAP = {
    product: 'product_page',
    cart: 'cart_page',
    checkout: 'checkout_page',
    search: 'search_page',
    home: 'home_page',
    collection: 'collection_page',
  };

  /**
   * Creates a new RecommendationAPI instance with injected dependencies.
   *
   * Initializes the API client by configuring the base URL from the application
   * configuration and setting up the HTTP client through the parent {@link BoostAPI} class.
   * All dependencies are automatically injected by the dependency injection framework.
   *
   * @param platformLoader - Platform loader for accessing platform-specific functionality
   * and customer data. Injected automatically.
   *
   * @param appService - Application service providing access to TAE app configuration,
   * including the recommendation API base URL. Injected automatically.
   *
   * @param b2bService - B2B service for adding business-to-business specific parameters
   * to recommendation requests. Injected automatically.
   *
   * @remarks
   * The constructor automatically configures the HTTP client's base URL using
   * `appService.TAEAppConfig.recommendUrl`, which should point to the Boost
   * recommendation API endpoint for the current shop.
   */
  constructor(
    protected platformLoader: PlatformLoader,
    protected appService: AppService,
    protected b2bService: B2BService
  ) {
    super(platformLoader);
    this.config.baseURL = this.appService.TAEAppConfig.recommendUrl;
  }

  /**
   * Builds and enhances query parameters for recommendation API requests.
   *
   * This method transforms the base query parameters through a functional pipeline that
   * automatically adds currency information and B2B-specific parameters. It serves as
   * a key extension point for Technical Support teams to inject shop-specific query
   * parameters without modifying the core request flow.
   *
   * The method uses functional composition (pipe) to apply transformations sequentially,
   * ensuring each enhancement step is clearly separated and maintainable.
   *
   * @param params - Base recommendation query parameters including pagination settings,
   * filters, and widget configuration. These are typically provided by the widget
   * implementation or recommendation controller.
   *
   * @returns Enhanced query parameters with currency settings, B2B configurations, and
   * any custom shop-specific parameters added through overrides. The returned object
   * contains all parameters ready for API request serialization.
   *
   * @remarks
   * The default implementation applies transformations in this order:
   * 1. **Currency parameters**: Adds current currency code and conversion settings via
   *    {@link BoostAPI.addCurrencyParams}
   * 2. **B2B parameters**: Adds customer group, pricing tier, and B2B-specific settings
   *    via {@link B2BService.addB2BRequestParams}
   *
   * This method is designed for extension - override it to add additional parameters
   * while preserving the default currency and B2B transformations by calling the
   * parent implementation.
   *
   * Performance note: The functional pipe approach has minimal overhead and allows
   * for efficient parameter transformation without unnecessary object copying.
   *
   * @example
   * Override to add custom pricing tier for wholesale customers:
   * ```typescript
   * protected buildRecommendationQueryParams(params: RecommendationQueryParams): RecommendationQueryParams {
   *   // Apply default transformations first
   *   const enhanced = super.buildRecommendationQueryParams(params);
   *
   *   // Add wholesale pricing parameters
   *   const customer = this.platformLoader.platform.customer;
   *   if (customer?.tags?.includes('wholesale')) {
   *     enhanced.pricing_tier = 'wholesale';
   *     enhanced.discount_level = 'tier-1';
   *   }
   *
   *   return enhanced;
   * }
   * ```
   *
   * @example
   * Override to add customer segment and personalization flags:
   * ```typescript
   * protected buildRecommendationQueryParams(params: RecommendationQueryParams): RecommendationQueryParams {
   *   const enhanced = super.buildRecommendationQueryParams(params);
   *
   *   // Add customer segment for personalized recommendations
   *   const customer = this.platformLoader.platform.customer;
   *   if (customer) {
   *     enhanced.customer_segment = customer.ordersCount > 5 ? 'loyal' : 'new';
   *     enhanced.personalization_enabled = true;
   *   }
   *
   *   // Add A/B test variant
   *   enhanced.ab_test_variant = this.getABTestVariant();
   *
   *   return enhanced;
   * }
   * ```
   *
   * @example
   * Override to add location-based filtering:
   * ```typescript
   * protected buildRecommendationQueryParams(params: RecommendationQueryParams): RecommendationQueryParams {
   *   const enhanced = super.buildRecommendationQueryParams(params);
   *
   *   // Add shipping zone for location-specific recommendations
   *   const shippingZone = this.getCustomerShippingZone();
   *   if (shippingZone) {
   *     enhanced.shipping_zone = shippingZone;
   *     enhanced.regional_products_only = true;
   *   }
   *
   *   return enhanced;
   * }
   * ```
   */
  protected buildRecommendationQueryParams(
    params: RecommendationQueryParams
  ): RecommendationQueryParams {
    return pipe(
      { ...params },
      (p) => this.addCurrencyParams(p),
      (p) => this.b2bService.addB2BRequestParams(p),
      (p) => this.addPageParam(p)
    );
  }

  /**
   * Adds page-specific parameter to enhance recommendation targeting.
   *
   * This method injects the current page context into recommendation requests,
   * allowing the API to optimize recommendations based on where the user is
   * browsing (product page, cart, checkout, etc.).
   *
   * @param params - Base parameters object to enhance with page information
   *
   * @returns Enhanced parameters with `pg` field containing the page-specific value
   * from {@link PAGE_PARAM_MAP}, or empty string if page is unknown
   *
   * @example
   * Override to add custom page detection logic:
   * ```typescript
   * protected addPageParam<P extends Record<string, unknown>>(params: P): P & { pg: string } {
   *   const enhanced = super.addPageParam(params);
   *
   *   // Add custom page detection for SPA routing
   *   if (!enhanced.pg && window.location.hash.includes('#/custom')) {
   *     enhanced.pg = 'custom_page';
   *   }
   *
   *   return enhanced;
   * }
   * ```
   */
  protected addPageParam<P extends Record<string, unknown>>(params: P): P & { pg: string } {
    // Cart-drawer widgets are page-agnostic: the drawer represents the cart and can be
    // opened from any page (home, collection, product, search). Reporting the underlying
    // page (e.g. collection_page) makes the backend scope page-contextual recommendation
    // types — notably hand-picked-products — to that page's collection, which returns no
    // matches. Always report the cart context for cart-drawer widgets so results are
    // consistent regardless of which page triggered the drawer.
    const wid = typeof params.wid === 'string' ? params.wid : '';
    if (wid.startsWith('cartdrawer')) {
      return {
        ...params,
        pg: this.PAGE_PARAM_MAP.cart,
      };
    }

    const page = this.platformLoader.platform.router.page;

    return {
      ...params,
      pg: page ? this.PAGE_PARAM_MAP[page] : '',
    };
  }

  /**
   * Builds and enhances the request body payload for recommendation API requests.
   *
   * This method provides an extension point for modifying the recommendation request
   * body before it's sent to the API. The default implementation returns the payload
   * unchanged, serving as a pass-through that allows Technical Support teams to
   * intercept and enhance the payload with shop-specific data, filters, or context.
   *
   * Unlike query parameters (which are URL-based), the body payload contains the core
   * recommendation context like product IDs, widget configuration, and customer data
   * that determines which products to recommend.
   *
   * @param bodyPayload - Base recommendation request payload containing widget type,
   * context data (product IDs, collection IDs, cart contents), and initial filter
   * settings. This payload is typically constructed by the recommendation controller
   * based on the widget's configuration and current page context.
   *
   * @returns Enhanced body payload with any shop-specific modifications applied.
   * The returned payload will be serialized to JSON and sent as the HTTP POST body.
   *
   * @remarks
   * The default implementation is intentionally minimal (pass-through), making this
   * a pure extension point. Override this method to:
   * - Add custom filtering criteria (tags, availability, price ranges)
   * - Inject additional context data (customer segments, session info)
   * - Modify recommendation algorithms or weights
   * - Add shop-specific business rules
   *
   * Common use cases include:
   * - Excluding specific product types or tags from recommendations
   * - Adding customer-specific context for personalization
   * - Injecting A/B test parameters
   * - Applying shop-specific inventory or availability filters
   *
   * Performance note: Keep payload modifications lightweight to maintain fast API
   * response times. Avoid heavy computations or synchronous API calls in this method.
   *
   * @example
   * Override to add custom product filtering:
   * ```typescript
   * protected buildRecommendationBodyPayload(bodyPayload: RecommendationPayload): RecommendationPayload {
   *   const enhanced = super.buildRecommendationBodyPayload(bodyPayload);
   *
   *   // Exclude out-of-stock and discontinued products
   *   enhanced.filters = {
   *     ...enhanced.filters,
   *     excludeTags: ['out-of-stock', 'discontinued', 'coming-soon'],
   *     inStock: true,
   *     published: true
   *   };
   *
   *   return enhanced;
   * }
   * ```
   *
   * @example
   * Override to add customer personalization context:
   * ```typescript
   * protected buildRecommendationBodyPayload(bodyPayload: RecommendationPayload): RecommendationPayload {
   *   const enhanced = super.buildRecommendationBodyPayload(bodyPayload);
   *
   *   // Add customer context for personalized recommendations
   *   const customer = this.platformLoader.platform.customer;
   *   if (customer) {
   *     enhanced.customerContext = {
   *       id: customer.id,
   *       segment: this.getCustomerSegment(customer),
   *       purchaseHistory: customer.ordersCount,
   *       preferredCategories: this.getCustomerPreferences(customer)
   *     };
   *   }
   *
   *   return enhanced;
   * }
   * ```
   *
   * @example
   * Override to add price range filtering based on cart value:
   * ```typescript
   * protected buildRecommendationBodyPayload(bodyPayload: RecommendationPayload): RecommendationPayload {
   *   const enhanced = super.buildRecommendationBodyPayload(bodyPayload);
   *
   *   // Add price filtering based on average cart value
   *   const cart = this.platformLoader.platform.cart;
   *   if (cart) {
   *     const avgItemPrice = cart.total_price / cart.item_count;
   *
   *     // Recommend products within similar price range
   *     enhanced.filters = {
   *       ...enhanced.filters,
   *       minPrice: avgItemPrice * 0.7,
   *       maxPrice: avgItemPrice * 1.5
   *     };
   *   }
   *
   *   return enhanced;
   * }
   * ```
   *
   * @example
   * Override to add A/B testing parameters:
   * ```typescript
   * protected buildRecommendationBodyPayload(bodyPayload: RecommendationPayload): RecommendationPayload {
   *   const enhanced = super.buildRecommendationBodyPayload(bodyPayload);
   *
   *   // Add A/B test variant information
   *   enhanced.experimentId = this.getActiveExperimentId();
   *   enhanced.variant = this.getAssignedVariant();
   *
   *   // Modify recommendation strategy based on variant
   *   if (enhanced.variant === 'similar-products') {
   *     enhanced.recommendationType = 'similarity-based';
   *   } else if (enhanced.variant === 'trending') {
   *     enhanced.recommendationType = 'popularity-based';
   *   }
   *
   *   return enhanced;
   * }
   * ```
   */
  protected buildRecommendationBodyPayload(
    bodyPayload: RecommendationPayload
  ): RecommendationPayload {
    // Allow extension point for body payload modifications
    return bodyPayload;
  }

  /**
   * Fetches product recommendations from the Boost recommendation API.
   *
   * This is the main entry point for making recommendation requests. It orchestrates
   * the request construction process by delegating to extensible protected methods for
   * parameter and payload building, then executes the HTTP POST request to the
   * recommendation endpoint.
   *
   * The method follows a clear pipeline:
   * 1. Enhances query parameters through {@link buildRecommendationQueryParams}
   * 2. Enhances request body through {@link buildRecommendationBodyPayload}
   * 3. Serializes parameters to query string format
   * 4. Executes POST request with enhanced parameters and body
   *
   * @param params - Query parameters for the recommendation request, including pagination
   * settings (limit, page), widget configuration (widgetId), and any base filtering
   * criteria. These parameters will be enhanced with currency, B2B settings, and custom
   * shop-specific parameters through the transformation pipeline.
   *
   * @param bodyPayload - Request body payload containing the recommendation context such
   * as product IDs, collection IDs, cart contents, widget type, and filtering rules.
   * This payload will be enhanced with any custom shop-specific data through the
   * transformation pipeline.
   *
   * @returns Promise that resolves to a {@link RecommendationResponse} containing an
   * array of recommended products with their metadata, pagination information, and
   * tracking data for analytics. The promise rejects if the API request fails.
   *
   * @remarks
   * Request flow:
   * - **Step 1**: Query parameters are transformed by {@link buildRecommendationQueryParams},
   *   which adds currency info, B2B parameters, and any custom query parameters
   * - **Step 2**: Body payload is transformed by {@link buildRecommendationBodyPayload},
   *   allowing injection of custom filters, context, and business rules
   * - **Step 3**: Parameters are serialized to URL query string format
   * - **Step 4**: HTTP POST request is made to `/?{queryString}` with enhanced body
   *
   * The method leverages the HTTP client from {@link BoostAPI} parent class, which
   * provides connection pooling, retry logic, and error handling.
   *
   * Performance considerations:
   * - Uses POST instead of GET to support large request bodies
   * - Parameter transformation is lightweight (no deep cloning)
   * - HTTP client uses connection pooling for efficiency
   * - Response is streamed and parsed efficiently
   *
   * Error handling:
   * - Network errors are propagated as rejected promises
   * - HTTP error responses (4xx, 5xx) are rejected with error details
   * - Inherits error handling behavior from {@link BoostAPI.post}
   *
   * @throws {Error} Network connectivity issues, timeout errors, or DNS resolution failures
   * @throws {HTTPError} HTTP 4xx client errors (invalid parameters, authentication issues)
   * @throws {HTTPError} HTTP 5xx server errors (API unavailable, internal server errors)
   *
   * @example
   * Fetch similar products for a specific product:
   * ```typescript
   * const response = await api.getRecommendation(
   *   { limit: 10, page: 1 },
   *   {
   *     productId: '12345',
   *     type: 'similar-products',
   *     widgetId: 'pdp-similar'
   *   }
   * );
   *
   * console.log(`Found ${response.products.length} recommendations`);
   * response.products.forEach(product => {
   *   console.log(`${product.title} - $${product.price}`);
   * });
   * ```
   *
   * @example
   * Fetch frequently bought together recommendations:
   * ```typescript
   * const response = await api.getRecommendation(
   *   { limit: 5 },
   *   {
   *     productId: '12345',
   *     type: 'frequently-bought-together',
   *     widgetId: 'pdp-fbt',
   *     filters: {
   *       inStock: true,
   *       published: true
   *     }
   *   }
   * );
   * ```
   *
   * @example
   * Fetch cart-based recommendations with pagination:
   * ```typescript
   * const response = await api.getRecommendation(
   *   { limit: 20, page: 1 },
   *   {
   *     type: 'cart-recommendations',
   *     widgetId: 'cart-upsell',
   *     cartProductIds: [123, 456, 789],
   *     filters: {
   *       excludeCartProducts: true,
   *       minPrice: 10,
   *       maxPrice: 100
   *     }
   *   }
   * );
   * ```
   */
  getRecommendation(params: RecommendationQueryParams, bodyPayload: RecommendationPayload) {
    const enhancedParams = this.buildRecommendationQueryParams(params);
    const enhancedBody = this.buildRecommendationBodyPayload(bodyPayload);
    const queryParams = this.buildDefaultQueryParams(enhancedParams);
    return this.post<RecommendationResponse>(`/?${queryParams}`, enhancedBody);
  }
}
